import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { AgentApiService } from 'src/api/ai/agent'
import { CallApiService } from 'src/api/ai/call'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import { loadAccountsSuccess } from '../accounts'
import { loadUsersSuccess } from '../users'
import { archiveAgent, cloneAgent, cloneAgentFailure, cloneAgentSuccess, createAgent, createAgentFailure, createAgentSuccess, loadAgents, loadAgentsFailure, loadAgentsSuccess, startCall, startCallFailure, startCallSuccess, unarchiveAgent, updateAgent, updateAgentFailure, updateAgentSuccess } from './agent.actions'

@Injectable()
export class AgentEffects {
  private actions$ = inject(Actions)
  private AgentApi = inject(AgentApiService)
  private CallApi = inject(CallApiService)
  private toaster = inject(ToasterService)


  loadAgents$ = createEffect(() => this.actions$.pipe(
    ofType(loadAgents),
    switchMap(() =>
      this.AgentApi.list().pipe(
        switchMap(({ data: {agents, users, accounts} }) => of(
          loadAgentsSuccess({ agents }),
          loadUsersSuccess({ users: users.map(user => ({ ...user, fullname: `${user.firstname} ${user.lastname}`.trim() })) }),
          loadAccountsSuccess({ accounts }),
        )),
        catchAndLog(this.toaster, 'Unable to fetch agents', loadAgentsFailure)))))

  createAgent$ = createEffect(() => this.actions$.pipe(
    ofType(createAgent),
    tap(() => this.toaster.progress('Creating agent...')),
    switchMap(action =>
      this.AgentApi.create(action.agent).pipe(
        map(({ data: agent }) => createAgentSuccess({ agent })),
        tap(() => this.toaster.success('Agent created successfully.')),
        catchAndLog(this.toaster, 'Unable to create agent', createAgentFailure)))))

  cloneAgent$ = createEffect(() => this.actions$.pipe(
    ofType(cloneAgent),
    tap(() => this.toaster.progress('Cloning agent...')),
    switchMap(action =>
      this.AgentApi.create(action.agent).pipe(
        map(({ data: agent }) => cloneAgentSuccess({ agent })),
        tap(() => this.toaster.success('Agent cloned successfully.')),
        catchAndLog(this.toaster, 'Unable to clone agent', cloneAgentFailure)))))

  updateAgent$ = createEffect(() => this.actions$.pipe(
    ofType(updateAgent),
    tap(() => this.toaster.progress('Updating agent...')),
    switchMap(action =>
      this.AgentApi.update(action.id, action.agent).pipe(
        map(({ data: agent }) => updateAgentSuccess({ agent })),
        tap(() => this.toaster.success('Agent updated successfully.')),
        catchAndLog(this.toaster, 'Unable to update agent', updateAgentFailure)))))

  startCall$ = createEffect(() => this.actions$.pipe(
    ofType(startCall),
    tap(() => this.toaster.progress('Scheduling a call ...')),
    switchMap(action =>
      this.CallApi.startCall({
        agent_id: action.agent_id,
        account: action.account,
        user_id: action.user_id,
        trader_id: action.trader_id,
        phone: action.phone,
        variableValues: action.variableValues,
        transferValues: action.transferValues,
        sendLinkAsked: action.sendLinkAsked,
      }).pipe(
        map(() => startCallSuccess()),
        tap(() => this.toaster.success('Call scheduled successfully.')),
        catchAndLog(this.toaster, 'Unable to start a call', startCallFailure)))))

  archiveAgent$ = createEffect(() => this.actions$.pipe(
    ofType(archiveAgent),
    tap(() => this.toaster.progress('Archiving agent...')),
    switchMap(action =>
      this.AgentApi.update(action.id, { archived: 1 }).pipe(
        map(({ data: agent }) => updateAgentSuccess({ agent })),
        tap(() => this.toaster.success('Agent archived successfully.')),
        catchAndLog(this.toaster, 'Unable to update agent', updateAgentFailure)))))

  unarchiveAgent$ = createEffect(() => this.actions$.pipe(
    ofType(unarchiveAgent),
    tap(() => this.toaster.progress('Unarchiving agent...')),
    switchMap(action =>
      this.AgentApi.update(action.id, { archived: 0 }).pipe(
        map(({ data: agent }) => updateAgentSuccess({ agent })),
        tap(() => this.toaster.success('Agent unarchived successfully.')),
        catchAndLog(this.toaster, 'Unable to update agent', updateAgentFailure)))))
}
